import { reportServerError } from '@/lib/utils/sentry';

import { isMfaRequiredError } from './mfaError';
import { handleMfaRequired } from './mfaRedirect';

/**
 * Spread the response body with the HTTP status so downstream consumers
 * (TanStack Query errors, the router error boundary) can branch on it.
 *
 * Exported for isolated unit testing; the primary consumer is `handleClientError`.
 */
export const attachHttpStatusToError = (
  body: unknown,
  response: Response | undefined
): Record<string, unknown> => {
  const status = response?.status;
  if (body && typeof body === 'object') {
    return { ...body, ...(status !== undefined && { status }) };
  }
  return {
    detail: typeof body === 'string' ? body : '',
    ...(status !== undefined && { status }),
  };
};

/**
 * hey-api client error interceptor. Reports server errors, normalizes the
 * error shape, and — when the backend's request-level MFA gate denies the
 * request (403 + mfa_required) — triggers the toast + redirect to login.
 */
export const handleClientError = (
  body: unknown,
  response: Response | undefined,
  request: Request | undefined
): Record<string, unknown> => {
  reportServerError(body, response, request);

  const errorWithStatus = attachHttpStatusToError(body, response);

  if (isMfaRequiredError(errorWithStatus)) {
    handleMfaRequired();
  }

  return errorWithStatus;
};
